import { fetchApi } from '@/config/api'
import { useAuthStore } from '@/stores/auth'
import type {
  MeaAutocompleteResult,
  MeaLimitCheckResult,
  UpdateMeaOptions,
} from '@/types/mea'

/**
 * Autocomplétion pour les références base (comme ancien système)
 */
export async function getMeaAutocomplete(
  term: string,
  referenceString?: string,
): Promise<MeaAutocompleteResult[]> {
  const authStore = useAuthStore()
  const vendeurId = authStore.user?.id_vendeur || authStore.user?.id

  if (!vendeurId) {
    throw new Error('Utilisateur non authentifié')
  }

  const query = `
    query MeaAutocomplete($input: MeaAutocompleteInput!) {
      meaAutocomplete(input: $input) {
        reference_base
      }
    }
  `

  const result = await fetchApi<{ meaAutocomplete: MeaAutocompleteResult[] }>(
    query,
    {
      input: {
        id_vendeur: Number(vendeurId),
        term,
        referenceString: referenceString || undefined,
      },
    },
  )

  return result.meaAutocomplete
}

/**
 * Mettre à jour les produits en MEA (réassort et/ou nouveautés)
 */
export async function updateMeaProducts(
  referenceBases: string[],
  options: UpdateMeaOptions,
): Promise<number> {
  const authStore = useAuthStore()
  const vendeurId = authStore.user?.id_vendeur || authStore.user?.id

  if (!vendeurId) {
    throw new Error('Utilisateur non authentifié')
  }

  const mutation = `
    mutation UpdateMeaProducts($input: UpdateMeaInput!) {
      updateMeaProducts(input: $input)
    }
  `

  const result = await fetchApi<{ updateMeaProducts: number }>(mutation, {
    input: {
      id_vendeur: Number(vendeurId),
      referenceBases,
      reassort: options.reassort,
      nouveautes: options.nouveautes,
    },
  })

  return result.updateMeaProducts
}

/**
 * Vérifier la limite mensuelle de MEA
 */
export async function checkMonthlyMeaLimit(limit = 50): Promise<boolean> {
  const authStore = useAuthStore()
  const vendeurId = authStore.user?.id_vendeur || authStore.user?.id

  if (!vendeurId) {
    throw new Error('Utilisateur non authentifié')
  }

  const query = `
    query CheckMonthlyMeaLimit($idVendeur: Int!, $limit: Int!) {
      checkMonthlyMeaLimit(id_vendeur: $idVendeur, limit: $limit)
    }
  `

  const result = await fetchApi<{ checkMonthlyMeaLimit: boolean }>(query, {
    idVendeur: Number(vendeurId),
    limit,
  })

  return result.checkMonthlyMeaLimit
}

/**
 * Obtenir le nombre de MEA du mois actuel
 */
export async function getCurrentMonthMeaCount(): Promise<number> {
  const authStore = useAuthStore()
  const vendeurId = authStore.user?.id_vendeur || authStore.user?.id

  if (!vendeurId) {
    throw new Error('Utilisateur non authentifié')
  }

  const query = `
    query GetCurrentMonthMeaCount($idVendeur: Int!) {
      getCurrentMonthMeaCount(id_vendeur: $idVendeur)
    }
  `

  const result = await fetchApi<{ getCurrentMonthMeaCount: number }>(query, {
    idVendeur: Number(vendeurId),
  })

  return result.getCurrentMonthMeaCount
}

/**
 * Vérifier la limite avant ajout (même logique que l'ancien système)
 */
export async function checkMonthlyMeaBeforeAdd(
  newReferenceCount: number,
  limit = 100,
): Promise<MeaLimitCheckResult> {
  const authStore = useAuthStore()
  const vendeurId = authStore.user?.id_vendeur || authStore.user?.id

  if (!vendeurId) {
    throw new Error('Utilisateur non authentifié')
  }

  const query = `
    query CheckMonthlyMeaBeforeAdd($idVendeur: Int!, $newReferenceCount: Int!, $limit: Int!) {
      checkMonthlyMeaBeforeAdd(id_vendeur: $idVendeur, newReferenceCount: $newReferenceCount, limit: $limit)
    }
  `

  const result = await fetchApi<{ checkMonthlyMeaBeforeAdd: string }>(query, {
    idVendeur: Number(vendeurId),
    newReferenceCount,
    limit,
  })

  return JSON.parse(result.checkMonthlyMeaBeforeAdd) as MeaLimitCheckResult
}
